// UpdateLeadDetails.jsx

import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactSelect from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { useUserAuth } from "../../context/UserAuthContext";
import {
  FiArrowLeft,
  FiSave,
  FiUser,
  FiPhone,
  FiCalendar,
  FiMapPin,
  FiCheckCircle,
  FiLoader,
  FiMessageCircle,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

const F = "'DM Sans', 'Segoe UI', sans-serif";
const T = {
  bg: "#f0f4f9",
  surface: "#ffffff",
  border: "#e2eaf4",
  text: "#0d1b2e",
  muted: "#64748b",
  accent: "#2563EB",
  accentBg: "#EBF4FF",
  accentHov: "#1d4ed8",
  success: "#059669",
  inputBg: "#f8fafc",
  label: "#374151",
};

/* ─────────────────── SHARED STYLES ─────────────────── */
const card = {
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 18,
  overflow: "hidden",
  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
  marginBottom: 20,
};

const inputBase = {
  width: "100%",
  height: 46,
  padding: "0 14px 0 44px",
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  fontSize: 14,
  fontFamily: F,
  color: T.text,
  background: T.inputBg,
  boxSizing: "border-box",
  outline: "none",
  transition: "border-color .15s, box-shadow .15s",
};

const labelStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  fontWeight: 600,
  color: T.label,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 6,
};

const iconWrap = (color) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  background: color + "18",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color,
  fontSize: 15,
  flexShrink: 0,
});

/* ─────────────────── SECTION HEADER ─────────────────── */
const SectionHeader = ({ icon, title, subtitle }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "18px 24px 16px",
      borderBottom: `1px solid ${T.border}`,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: T.accentBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: T.accent,
        fontSize: 18,
      }}
    >
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: T.muted, marginTop: 1 }}>
          {subtitle}
        </div>
      )}
    </div>
  </div>
);

/* ─────────────────── FIELD WRAPPER ─────────────────── */
const Field = ({ icon, iconColor = T.accent, label, children }) => (
  <div style={{ marginBottom: 20 }}>
    <label style={labelStyle}>
      <span style={iconWrap(iconColor)}>{icon}</span>
      {label}
    </label>
    {children}
  </div>
);

/* ─────────────────── INPUT WITH ICON ─────────────────── */
const IconInput = ({ icon, iconColor, focused, name, ...props }) => (
  <div style={{ position: "relative" }}>
    <span
      style={{
        position: "absolute",
        left: 13,
        top: "50%",
        transform: "translateY(-50%)",
        color: focused === name ? iconColor : T.muted,
        fontSize: 15,
        pointerEvents: "none",
        transition: "color .15s",
      }}
    >
      {icon}
    </span>
    <input
      name={name}
      style={{
        ...inputBase,
        borderColor: focused === name ? T.accent : T.border,
        boxShadow: focused === name ? `0 0 0 3px ${T.accent}22` : "none",
      }}
      {...props}
    />
  </div>
);

/* ─────────────────── REACT-SELECT STYLES ─────────────────── */
const rsStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    border: `1px solid ${state.isFocused ? T.accent : T.border}`,
    borderRadius: 10,
    background: T.inputBg,
    boxShadow: state.isFocused ? `0 0 0 3px ${T.accent}22` : "none",
    fontFamily: F,
    fontSize: 14,
    cursor: "pointer",
    paddingLeft: 32,
    "&:hover": { borderColor: T.accent },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    border: `1px solid ${T.border}`,
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected
      ? T.accent
      : state.isFocused
        ? T.accentBg
        : "#fff",
    color: state.isSelected ? "#fff" : T.text,
    fontSize: 13,
    fontFamily: F,
    cursor: "pointer",
    padding: "10px 14px",
  }),
  placeholder: (base) => ({ ...base, color: T.muted, fontSize: 13 }),
  singleValue: (base) => ({ ...base, color: T.text, fontSize: 14 }),
  indicatorSeparator: () => ({ display: "none" }),
};

/* ═════════════════════════════════════════════════════
   MAIN COMPONENT
═════════════════════════════════════════════════════ */
const UpdateLeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useUserAuth();

  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [focused, setFocused] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    mob_no: "",
    whatsapp_no: "",
    event_date: "",
    city_id: "",
  });

  /* ── fetches ── */
  const fetchCities = async () => {
    try {
      setCityLoading(true);
      const res = await axios.get(`${base_url}/api/v1/cities/cache`);
      setCities(
        res?.data?.data?.map((c) => ({ value: c.id, label: c.name })) || [],
      );
    } catch (e) {
      console.log(e);
    } finally {
      setCityLoading(false);
    }
  };

  const getLeadDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/leads/single/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res?.data?.data;
      setFormData({
        name: data?.name || "",
        mob_no: data?.mob_no || "",
        whatsapp_no: data?.whatsapp_no || "",
        event_date: data?.event_date
          ? new Date(Number(data.event_date) * 1000).toISOString().slice(0, 10)
          : "",
        city_id: data?.city_id || "",
      });
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
    getLeadDetails();
  }, [id]);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCityChange = (sel) =>
    setFormData((prev) => ({ ...prev, city_id: sel?.value || "" }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.put(
        `${base_url}/api/v1/leads/update-lead-details/${id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        navigate(-1);
      }, 1200);
    } catch (e) {
      console.log(e);
      alert("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="page-wrapper">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            gap: 12,
          }}
        >
          <FiLoader
            size={24}
            color={T.accent}
            style={{ animation: "spin .7s linear infinite" }}
          />
          <span style={{ color: T.muted, fontFamily: F, fontSize: 15 }}>
            Loading lead details…
          </span>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes fadeIn  { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
        .uld-wrap          { animation: fadeIn .22s ease; }
        .uld-btn-save:hover { background: ${T.accentHov} !important; }
        .uld-btn-back:hover { background: ${T.inputBg} !important; }
        input[type=date]::-webkit-calendar-picker-indicator { opacity:.5; cursor:pointer; }
      `}</style>

        <div
          className="uld-wrap"
          style={{
            background: T.bg,
            minHeight: "100vh",
            fontFamily: F,
            padding: "24px 20px 60px",
          }}
        >
          <div style={{ margin: "0 auto" }}>
            {/* ── HEADER CARD ── */}
            <div
              style={{
                background: "linear-gradient(135deg, #0f4c8a 0%, #051e3e 100%)",
                borderRadius: 20,
                padding: "24px 28px",
                marginBottom: 20,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -70,
                  right: -70,
                  width: 220,
                  height: 220,
                  borderRadius: "50%",
                  background: "rgba(99,179,237,0.1)",
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: -40,
                  left: 40,
                  width: 140,
                  height: 140,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.04)",
                  pointerEvents: "none",
                }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: "#fff",
                      letterSpacing: "-0.3px",
                      marginBottom: 6,
                    }}
                  >
                    Update Lead Details
                  </div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                    Edit contact info, event date and city
                  </div>
                </div>
                <button
                  className="uld-btn-back"
                  onClick={() => navigate(-1)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "9px 18px",
                    borderRadius: 10,
                    border: "0.5px solid rgba(255,255,255,0.3)",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: F,
                    transition: "background .15s",
                  }}
                >
                  <FiArrowLeft size={15} /> Back
                </button>
              </div>
            </div>

            {/* ── FORM ── */}
            <form onSubmit={handleSubmit}>
              {/* SECTION — Contact Details */}
              <div style={card}>
                <SectionHeader
                  icon={<FiUser />}
                  title="Contact Details"
                  subtitle="Name and phone numbers of the lead"
                />
                <div style={{ padding: "22px 24px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(240px, 1fr))",
                      gap: "0 28px",
                    }}
                  >
                    {/* NAME */}
                    <Field
                      icon={<FiUser size={15} />}
                      iconColor="#2563EB"
                      label="Full Name"
                    >
                      <IconInput
                        icon={<FiUser size={14} />}
                        iconColor="#2563EB"
                        focused={focused}
                        name="name"
                        type="text"
                        placeholder="Enter full name"
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => setFocused("name")}
                        onBlur={() => setFocused("")}
                      />
                    </Field>

                    {/* MOBILE */}
                    <Field
                      icon={<FiPhone size={15} />}
                      iconColor="#10b981"
                      label="Mobile Number"
                    >
                      <IconInput
                        icon={<FiPhone size={14} />}
                        iconColor="#10b981"
                        focused={focused}
                        name="mob_no"
                        type="text"
                        placeholder="Enter mobile number"
                        value={formData.mob_no}
                        onChange={handleChange}
                        onFocus={() => setFocused("mob_no")}
                        onBlur={() => setFocused("")}
                      />
                    </Field>

                    {/* WHATSAPP */}
                    <Field
                      icon={<FaWhatsapp size={15} />}
                      iconColor="#25D366"
                      label="WhatsApp Number"
                    >
                      <IconInput
                        icon={<FaWhatsapp size={14} />}
                        iconColor="#25D366"
                        focused={focused}
                        name="whatsapp_no"
                        type="text"
                        placeholder="Enter WhatsApp number"
                        value={formData.whatsapp_no}
                        onChange={handleChange}
                        onFocus={() => setFocused("whatsapp_no")}
                        onBlur={() => setFocused("")}
                      />
                    </Field>
                  </div>
                </div>
              </div>

              {/* SECTION — Event & Location */}
              <div style={card}>
                <SectionHeader
                  icon={<FiCalendar />}
                  title="Event & Location"
                  subtitle="Event date and city details"
                />
                <div style={{ padding: "22px 24px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(240px, 1fr))",
                      gap: "0 28px",
                    }}
                  >
                    {/* EVENT DATE */}
                    <Field
                      icon={<FiCalendar size={15} />}
                      iconColor="#f59e0b"
                      label="Event Date"
                    >
                      <div style={{ position: "relative" }}>
                        <span
                          style={{
                            position: "absolute",
                            left: 13,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color:
                              focused === "event_date" ? "#f59e0b" : T.muted,
                            fontSize: 15,
                            pointerEvents: "none",
                            transition: "color .15s",
                          }}
                        >
                          <FiCalendar size={14} />
                        </span>
                        <input
                          type="date"
                          name="event_date"
                          value={formData.event_date}
                          onChange={handleChange}
                          onFocus={() => setFocused("event_date")}
                          onBlur={() => setFocused("")}
                          style={{
                            ...inputBase,
                            borderColor:
                              focused === "event_date" ? T.accent : T.border,
                            boxShadow:
                              focused === "event_date"
                                ? `0 0 0 3px ${T.accent}22`
                                : "none",
                          }}
                        />
                      </div>
                    </Field>

                    {/* CITY */}
                    <Field
                      icon={<FiMapPin size={15} />}
                      iconColor="#8b5cf6"
                      label="City"
                    >
                      <div style={{ position: "relative" }}>
                        <span
                          style={{
                            position: "absolute",
                            left: 13,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: T.muted,
                            fontSize: 15,
                            pointerEvents: "none",
                            zIndex: 2,
                          }}
                        >
                          <FiMapPin size={14} />
                        </span>
                        <ReactSelect
                          options={cities}
                          styles={rsStyles}
                          value={
                            cities.find(
                              (c) =>
                                String(c.value) === String(formData.city_id),
                            ) || null
                          }
                          onChange={handleCityChange}
                          placeholder={
                            cityLoading ? "Loading cities…" : "Select city"
                          }
                          isSearchable
                          isLoading={cityLoading}
                        />
                      </div>
                    </Field>
                  </div>
                </div>
              </div>

              {/* ── SAVE BAR ── */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 12,
                  padding: "16px 24px",
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 16,
                  boxShadow: "0 -2px 16px rgba(0,0,0,0.04)",
                }}
              >
                <button
                  type="button"
                  className="uld-btn-back"
                  onClick={() => navigate(-1)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "11px 22px",
                    borderRadius: 10,
                    border: `1px solid ${T.border}`,
                    background: T.surface,
                    color: T.muted,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer",
                    fontFamily: F,
                    transition: "background .15s",
                  }}
                >
                  <FiArrowLeft size={15} /> Cancel
                </button>

                <button
                  type="submit"
                  className="uld-btn-save"
                  disabled={saving || saved}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "11px 28px",
                    borderRadius: 10,
                    border: "none",
                    background: saved ? T.success : T.accent,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: saving ? "not-allowed" : "pointer",
                    fontFamily: F,
                    transition: "background .2s",
                    opacity: saving ? 0.85 : 1,
                    minWidth: 160,
                    justifyContent: "center",
                  }}
                >
                  {saving ? (
                    <>
                      <FiLoader
                        size={15}
                        style={{ animation: "spin .7s linear infinite" }}
                      />{" "}
                      Saving…
                    </>
                  ) : saved ? (
                    <>
                      <FiCheckCircle size={15} /> Updated!
                    </>
                  ) : (
                    <>
                      <FiSave size={15} /> Update Details
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateLeadDetails;
